
import { loadGameData } from "@/utils/gameQueries";
import { useToast } from "@/components/ui/use-toast";
import { WordPair } from "@/types/game";

/**
 * Loads game data and handles related state updates
 */
export const loadGameDataWithStateUpdates = async (
  gameId: string | null,
  userId: string | null,
  toast: ReturnType<typeof useToast>["toast"],
  setLoading: (loading: boolean) => void,
  setGameStatus: (status: string) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setRounds: (rounds: WordPair[]) => void,
  setUsedWords: (usedWords: Set<string>) => void,
  setIsOwner: (isOwner: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  generateBotWordForCurrentRound: (rounds: WordPair[]) => Promise<void>
) => {
  if (!gameId) {
    setLoading(false);
    return { success: false };
  }

  try {
    setLoading(true);
    
    const { game, rounds: loadedRounds, usedWords: loadedUsedWords, currentRound, error } = await loadGameData(gameId);
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error
      });
      setLoading(false);
      return { success: false };
    }
    
    if (!game) {
      toast({
        variant: "destructive",
        title: "Game not found",
        description: "The game you're looking for doesn't exist."
      });
      setLoading(false);
      return { success: false };
    }

    // Check if user is the owner of this game
    setIsOwner(game.user_id === userId);

    // Set game status
    setGameStatus(game.status);

    // Game is over
    if (game.status === "completed" || game.status === "diverged") {
      setGameOver(true);
      // If the game is diverged, we don't set convergenceRound
      if (game.status === "completed") {
        setConvergenceRound(loadedRounds.length);
      }
    }
    
    setRounds(loadedRounds);
    setUsedWords(loadedUsedWords);
    
    // Check if the current round has an AI word
    if (currentRound && currentRound.ai_word && game.status !== "completed" && game.status !== "diverged") {
      // Bot has already chosen a word for this round
      setBotHasChosenWord(true);
    } else if (currentRound && !currentRound.ai_word && loadedRounds.length > 0 && game.status !== "completed" && game.status !== "diverged") {
      // We need to generate a word for the current round based on the previous round
      await generateBotWordForCurrentRound(loadedRounds);
    }

    setLoading(false);
    return { success: true };
  } catch (error) {
    console.error("Error loading game:", error);
    toast({
      variant: "destructive",
      title: "Error loading game",
      description: "There was a problem loading the game data."
    });
    setLoading(false);
    return { success: false };
  }
};
